import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router'
import {
  LayoutDashboard,
  Server,
  Activity,
  FileText,
  Settings,
  KeyRound,
  Zap,
  LogOut,
  ChevronUp,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

export default function Sidebar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const displayName = user?.name || user?.email?.split('@')[0] || 'Operator'
  const initials = displayName
    .split(/[\s@]+/)
    .slice(0, 2)
    .map((s: string) => s[0]?.toUpperCase())
    .join('')

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleLogout = () => {
    setMenuOpen(false)
    logout()
    navigate('/login')
  }

  return (
    <aside
      style={{
        width: 'var(--sidebar-width)',
        minWidth: 'var(--sidebar-width)',
        height: '100vh',
        backgroundColor: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Zeus v2 Logo */}
      <div style={{
        padding: '20px 20px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--brand)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Zap size={18} color="#fff" fill="#fff" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{
            fontSize: '16px',
            fontWeight: 700,
            color: 'var(--text-primary)',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            Zeus v2
          </span>
          <span style={{
            fontSize: '10px',
            fontWeight: 500,
            color: 'var(--text-tertiary)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            Platform Control
          </span>
          <span style={{
            fontSize: '9px',
            fontWeight: 500,
            color: 'var(--text-tertiary)',
            opacity: 0.6,
            fontFamily: 'var(--font-mono)',
          }}>
            v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?'}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{
        flex: 1,
        paddingTop: '8px',
        overflowY: 'auto',
      }}>
        <SidebarNavLink to="/dashboard" icon={<LayoutDashboard size={18} />}>
          Dashboard
        </SidebarNavLink>
        <SidebarNavLink to="/tenants" icon={<Server size={18} />}>
          Tenants
        </SidebarNavLink>
        <SidebarNavLink to="/jobs" icon={<Activity size={18} />}>
          Jobs
        </SidebarNavLink>
        <SidebarNavLink to="/audit" icon={<FileText size={18} />}>
          Audit Log
        </SidebarNavLink>
        <SidebarNavLink to="/settings" icon={<Settings size={18} />}>
          Settings
        </SidebarNavLink>
        <SidebarNavLink to="/secrets" icon={<KeyRound size={18} />}>
          Secrets
        </SidebarNavLink>
      </nav>

      {/* User footer with menu */}
      <div ref={menuRef} style={{ position: 'relative' }}>
        {menuOpen && (
          <div
            className="pop-in"
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '8px',
              right: '8px',
              marginBottom: '4px',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-elevated)',
              overflow: 'hidden',
              zIndex: 1000,
            }}
          >
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '2px',
              }}>
                {user?.name || 'User'}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'var(--text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {user?.email}
              </div>
            </div>
            <div style={{ padding: '4px' }}>
              <button
                onClick={handleLogout}
                className="btn-ghost"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--status-unhealthy)',
                  textAlign: 'left',
                }}
              >
                <LogOut size={14} />
                Sign Out
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => setMenuOpen(prev => !prev)}
          style={{
            width: '100%',
            borderTop: '1px solid var(--border-subtle)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            backgroundColor: menuOpen ? 'var(--bg-tertiary)' : 'transparent',
            border: 'none',
            textAlign: 'left',
          }}
        >
          <div style={{
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--brand-dim)',
            color: 'var(--brand)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 700,
            flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayName}
            </div>
            <div style={{
              fontSize: '10px',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}>
              {user?.email || 'Zeus v2'}
            </div>
          </div>
          <ChevronUp
            size={12}
            style={{
              color: 'var(--text-tertiary)',
              transition: 'transform 150ms',
              transform: menuOpen ? 'rotate(180deg)' : 'none',
              flexShrink: 0,
            }}
          />
        </button>
      </div>
    </aside>
  )
}

function SidebarNavLink({
  to,
  icon,
  children,
}: {
  to: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      end={to === '/dashboard'}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 16px',
        margin: '2px 8px',
        borderRadius: 'var(--radius-md)',
        fontSize: '13px',
        fontWeight: isActive ? 600 : 500,
        textDecoration: 'none',
        color: isActive ? 'var(--brand)' : 'var(--text-secondary)',
        backgroundColor: isActive ? 'var(--brand-dim)' : 'transparent',
        borderLeft: isActive ? '3px solid var(--brand)' : '3px solid transparent',
        transition: 'color 120ms ease, background-color 120ms ease',
      })}
      className="nav-link"
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
      </span>
      {children}
    </NavLink>
  )
}
